import { ConfigService } from '@config/config.service';
import { LeadsListActions, LeadsListActionTypes } from '@crm/ngrx/actions/leads-list.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { TaskListRequest } from '@services/associations/model/taskListRequest';
import { TaskResponse } from '@services/associations/model/taskResponse';
import { PaginationModel } from '@shared/models/pagination.model';

export const adapter: EntityAdapter<TaskResponse> = createEntityAdapter<TaskResponse>();

export interface State extends EntityState<TaskResponse> {
  loading: boolean,
  filter: TaskListRequest,
  pagination: PaginationModel,
}

export const initialState: State = adapter.getInitialState({
  loading: false,
  filter: {},
  pagination: {
    first: true,
    last: false,
    number: 0,
    numberOfElements: 0,
    size: new ConfigService().paginationConfig.pageSize,
    totalElements: 0,
  },
});

export function reducer(state = initialState, action: LeadsListActions): State {
  switch (action.type) {
    /** Load Tasks */
    case LeadsListActionTypes.LoadTasks:
      return {...state, loading: !action.payload?.dontShowLoading};

    case LeadsListActionTypes.LoadTasksSuccess:
      return adapter.setAll(action.payload.content!, {
        ...state,
        loading: false,
        pagination: {
          size: action.payload.size,
          last: action.payload.last,
          totalElements: action.payload.totalElements,
          numberOfElements: action.payload.numberOfElements,
          first: action.payload.first,
          number: action.payload.number,
        },
      });

    case LeadsListActionTypes.LoadTasksFailure:
      return {...state, loading: false};

    /** Edit Task Success (dispatch From Board Effects) */
    case LeadsListActionTypes.EditTasksSuccess:
      return adapter.updateOne({
        id: action.payload.id!,
        changes: action.payload
      }, {...state});

    /** Edit Multiple Tasks */
    case LeadsListActionTypes.EditMultipleTasks:
      return adapter.updateMany(action.payload.map((task: TaskResponse) => Object.assign({}, {id: task.id!, changes: task})), {...state});

    /** Delete List Task */
    case LeadsListActionTypes.DeleteTasks:
      return {...state};

    case LeadsListActionTypes.DeleteTasksSuccess:
      return adapter.removeOne(action.payload, {...state});

    case LeadsListActionTypes.DeleteTasksFailure:
      return {...state};

    /** Set Filter */
    case LeadsListActionTypes.SetFilter:
      return {...state, filter: action.payload};

    case LeadsListActionTypes.UpdateFilter:
      return {...state, filter: {...state.filter, ...action.payload}};

    /** Clear Filter */
    case LeadsListActionTypes.ClearFilter:
      return {...state, filter: initialState.filter};

    /** Change Page */
    case LeadsListActionTypes.ChangePage:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          number: action.payload.number,
          size: action.payload.size,
        }
      };

    /** Set Initial Pagination */
    case LeadsListActionTypes.SetInitialPagination:
      return {...state, pagination: initialState.pagination};

    /** Set Initial State */
    case LeadsListActionTypes.SetInitialState:
      return {...initialState};

    default:
      return state;
  }
}

export const {
  selectIds: selectTasksIds,
  selectEntities: selectTasksEntities,
  selectAll: selectAllTasks,
  selectTotal: selectTasksTotal,
} = adapter.getSelectors();
